import { normalizeHistoryPoints } from "@/data-converters/chart-history";

export interface IChartHistoryPoint {
  x: Date;
  y: number;
}

export interface IChartHistoryCacheEntry {
  suffix: string;
  baseHistories: IChartHistoryPoint[];
  historyCurrentTime: number;
}

export type IChartHistoryFetchPlan =
  | { type: "none" }
  | { type: "full" }
  | { type: "incremental"; timeFrom: number };

export interface IChartHistorySnapshot {
  currentTime: number;
  lastPower: number;
}

export function createChartHistoryCacheStore(options: {
  maxKeys: number;
  maxIntervals: number;
}): {
  read: (key: string | undefined) => IChartHistoryCacheEntry | undefined;
  write: (key: string | undefined, entry: IChartHistoryCacheEntry) => void;
} {
  const cache = new Map<string, IChartHistoryCacheEntry>();

  const cloneEntry = (entry: IChartHistoryCacheEntry): IChartHistoryCacheEntry => {
    return {
      suffix: entry.suffix,
      baseHistories: normalizeHistoryPoints(entry.baseHistories, options.maxIntervals),
      historyCurrentTime: entry.historyCurrentTime,
    };
  };

  const enforceLimit = () => {
    while (cache.size > options.maxKeys) {
      const oldestKey = cache.keys().next().value;
      if (!oldestKey) return;
      cache.delete(oldestKey);
    }
  };

  return {
    read: (key: string | undefined): IChartHistoryCacheEntry | undefined => {
      if (!key) return undefined;

      const found = cache.get(key);
      if (!found) return undefined;

      const cloned = cloneEntry(found);
      cache.set(key, cloned);
      return cloned;
    },
    write: (key: string | undefined, entry: IChartHistoryCacheEntry): void => {
      if (!key) return;
      cache.set(key, cloneEntry(entry));
      enforceLimit();
    },
  };
}

export function resolveChartHistoryFetchPlan({
  cacheEntry,
  intervalSec,
  currentTimeSec,
  staleIntervals,
}: {
  cacheEntry: IChartHistoryCacheEntry | undefined;
  intervalSec: number;
  currentTimeSec: number;
  staleIntervals: number;
}): IChartHistoryFetchPlan {
  if (cacheEntry == undefined || cacheEntry.baseHistories.length == 0) {
    return { type: "full" };
  }

  const safeInterval =
    Number.isFinite(intervalSec) && intervalSec > 0 ? Math.floor(intervalSec) : 1;
  const safeCurrentTime =
    Number.isFinite(currentTimeSec) && currentTimeSec > 0
      ? Math.floor(currentTimeSec)
      : 0;
  const lastBaseTimestampSec = resolveLastBaseTimestampSec(cacheEntry.baseHistories);

  if (safeCurrentTime <= 0 || lastBaseTimestampSec <= 0) {
    return { type: "full" };
  }

  if (safeCurrentTime - lastBaseTimestampSec > safeInterval * staleIntervals) {
    return { type: "full" };
  }

  if (lastBaseTimestampSec > safeCurrentTime) {
    return { type: "none" };
  }

  return { type: "incremental", timeFrom: lastBaseTimestampSec };
}

export function buildChartHistoryView(
  cacheEntry: IChartHistoryCacheEntry,
  maxIntervals: number,
  snapshot: IChartHistorySnapshot | undefined
): { suffix: string; histories: IChartHistoryPoint[] } {
  const histories = normalizeHistoryPoints(cacheEntry.baseHistories, maxIntervals);
  if (histories.length == 0) {
    return {
      suffix: cacheEntry.suffix,
      histories,
    };
  }

  const nextHistories = histories.map((point) => ({ x: new Date(point.x), y: point.y }));
  if (
    snapshot != undefined &&
    Number.isFinite(snapshot.currentTime) &&
    snapshot.currentTime > 0
  ) {
    const lastIdx = nextHistories.length - 1;
    const lastBaseTimestampSec = toTimestampSec(nextHistories[lastIdx].x);
    const statsCurrentTimeSec = Math.floor(snapshot.currentTime);

    if (lastBaseTimestampSec > statsCurrentTimeSec) {
      nextHistories[lastIdx] = {
        x: new Date(statsCurrentTimeSec * 1000),
        y: mapHashratePowerToChartY(snapshot.lastPower, cacheEntry.suffix),
      };
    }
  }

  return {
    suffix: cacheEntry.suffix,
    histories: nextHistories,
  };
}

export function rescaleChartHistoryPoints(
  histories: IChartHistoryPoint[],
  fromSuffix: string,
  toSuffix: string
): IChartHistoryPoint[] {
  const fromPow = resolveSuffixPow(fromSuffix);
  const toPow = resolveSuffixPow(toSuffix);
  if (fromPow == undefined || toPow == undefined || fromPow == toPow) {
    return histories.map((point) => ({ x: new Date(point.x), y: point.y }));
  }

  const ratio = Math.pow(10, fromPow - toPow);
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return histories.map((point) => ({ x: new Date(point.x), y: point.y }));
  }

  return histories.map((point) => ({
    x: new Date(point.x),
    y: Number((point.y * ratio).toPrecision(12)),
  }));
}

export function mapHashratePowerToChartY(lastPower: number, suffix: string): number {
  if (!Number.isFinite(lastPower)) return 0;

  const suffixPow = resolveSuffixPow(suffix);
  if (suffixPow == undefined) return Number(lastPower.toPrecision(12));

  const scaled = lastPower / Math.pow(10, suffixPow);
  if (!Number.isFinite(scaled)) return 0;
  return Number(scaled.toPrecision(12));
}

export function toTimestampSec(value: Date): number {
  const timestampMs = value.getTime();
  if (!Number.isFinite(timestampMs) || timestampMs <= 0) return 0;
  return Math.floor(timestampMs / 1000);
}

function resolveLastBaseTimestampSec(histories: IChartHistoryPoint[]): number {
  if (histories.length == 0) return 0;
  return toTimestampSec(histories[histories.length - 1].x);
}

function resolveSuffixPow(suffix: string): number | undefined {
  const normalized = String(suffix ?? "").trim().toUpperCase();
  if (normalized == "H/S") return 0;
  if (normalized == "KH/S") return 3;
  if (normalized == "MH/S") return 6;
  if (normalized == "GH/S") return 9;
  if (normalized == "TH/S") return 12;
  if (normalized == "PH/S") return 15;
  if (normalized == "EH/S") return 18;
  if (normalized == "ZH/S") return 21;
  if (normalized == "YH/S") return 24;
  return undefined;
}
